import { useState, useEffect } from "react";
import { Send, MapPin, Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import AnimatedSection from "@/components/AnimatedSection";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ContactPage() {
  const [pageData, setPageData] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", company: "", phone: "", message: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => { axios.get(`${API}/pages/contact`).then(r => setPageData(r.data)).catch(() => {}); }, []);

  const getSection = (key, fallback = "") => {
    if (!pageData) return fallback;
    const s = pageData.sections?.find(s => s.key === key);
    return s?.value || fallback;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in all required fields");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message sent! We'll get back to you soon.");
      setForm({ name: "", email: "", company: "", phone: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    }
    setSubmitting(false);
  };

  return (
    <div data-testid="contact-page">
      {/* Hero */}
      <section className="pt-32 pb-12 md:pt-40 md:pb-16 px-6 md:px-12" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto">
          <AnimatedSection>
            <span className="text-xs font-semibold tracking-wider uppercase text-amber mb-4 block">Contact Us</span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6 max-w-4xl">
              {getSection("headline", "Your AI Transformation Starts With One Conversation.")}
            </h1>
            <p className="text-base md:text-lg text-slate-300 max-w-2xl">
              {getSection("subheadline", "Tell us where your business is stuck \u2014 we\u2019ll show you exactly how AI can move it forward.")}
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Form + Info */}
      <AnimatedSection>
        <section className="pb-20 md:pb-32 px-6 md:px-12" data-testid="contact-form-section">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
              {/* Form */}
              <div className="lg:col-span-7">
                <form onSubmit={handleSubmit} className="relative rounded-3xl border border-white/10 bg-white/[0.03] backdrop-blur-xl p-10 shadow-2xl shadow-indigo-500/5" data-testid="contact-form">
                  {/* Glassmorphic inner glow */}
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-white/[0.04] to-transparent pointer-events-none" />
                  
                  <div className="relative">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                      <div>
                        <label className="block text-sm font-medium text-white mb-2">Name *</label>
                        <input
                          type="text"
                          data-testid="contact-name"
                          value={form.name}
                          onChange={e => setForm({ ...form, name: e.target.value })}
                          placeholder="Your name"
                          className="w-full bg-white/[0.05] border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-slate-500 focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-500/40 outline-none transition-all backdrop-blur-sm"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-white mb-2">Email *</label>
                        <input
                          type="email"
                          data-testid="contact-email"
                          value={form.email}
                          onChange={e => setForm({ ...form, email: e.target.value })}
                          placeholder="you@company.com"
                          className="w-full bg-white/[0.05] border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-slate-500 focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-500/40 outline-none transition-all backdrop-blur-sm"
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-white mb-2">Company</label>
                        <input
                          type="text"
                          data-testid="contact-company"
                          value={form.company}
                          onChange={e => setForm({ ...form, company: e.target.value })}
                          placeholder="Company name"
                          className="w-full bg-white/[0.05] border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-slate-500 focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-500/40 outline-none transition-all backdrop-blur-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-white mb-2">Phone</label>
                        <input
                          type="tel"
                          data-testid="contact-phone"
                          value={form.phone}
                          onChange={e => setForm({ ...form, phone: e.target.value })}
                          placeholder="+1 (555) 000-0000"
                          className="w-full bg-white/[0.05] border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-slate-500 focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-500/40 outline-none transition-all backdrop-blur-sm"
                        />
                      </div>
                    </div>
                    <div className="mb-6">
                      <label className="block text-sm font-medium text-white mb-2">Message *</label>
                      <textarea
                        data-testid="contact-message"
                        value={form.message}
                        onChange={e => setForm({ ...form, message: e.target.value })}
                        placeholder="Tell us about your project or challenge..."
                        rows={5}
                        className="w-full bg-white/[0.05] border border-white/10 rounded-lg px-4 py-3 text-white placeholder:text-slate-500 focus:ring-2 focus:ring-indigo-500/40 focus:border-indigo-500/40 outline-none transition-all backdrop-blur-sm resize-none"
                        required
                      />
                    </div>
                    <button
                      type="submit"
                      data-testid="contact-submit-btn"
                      disabled={submitting}
                      className="inline-flex items-center gap-2 bg-indigo-600 text-white hover:bg-indigo-500 rounded-full px-8 py-3.5 font-semibold transition-all shadow-lg shadow-indigo-600/25 active:scale-95 disabled:opacity-50"
                    >
                      {submitting ? "Sending..." : "Send Message"} <Send size={18} />
                    </button>
                  </div>
                </form>
              </div>

              {/* Info */}
              <div className="lg:col-span-5">
                <div className="space-y-6">
                  <div className="relative rounded-3xl border border-indigo-500/20 bg-white/[0.03] backdrop-blur-xl p-10 shadow-2xl shadow-indigo-500/5">
                    {/* Glassmorphic inner glow */}
                    <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-indigo-500/[0.04] to-transparent pointer-events-none" />
                    
                    <div className="relative">
                      <h3 className="font-heading font-bold text-white text-xl mb-4">Let's Connect</h3>
                      <p className="text-slate-300 text-sm leading-relaxed mb-6">
                        No jargon. No hard selling. Just a straightforward conversation about what AI can do for your business.
                      </p>
                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <Mail size={18} className="text-indigo-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs text-slate-400 uppercase tracking-wider font-medium mb-1">Email</p>
                            <a href="mailto:info@brgt.ai" className="text-white hover:text-indigo-400 text-sm transition-colors">
                              info@brgt.ai
                            </a>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <MapPin size={18} className="text-indigo-400 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="text-xs text-slate-400 uppercase tracking-wider font-medium mb-1">Locations</p>
                            <p className="text-slate-300 text-sm">
                              New York, USA • UK • UAE • CAN • GER • IND
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="relative rounded-3xl border border-indigo-500/15 bg-indigo-500/[0.08] backdrop-blur-xl p-8">
                    {/* Glassmorphic inner glow */}
                    <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-indigo-500/[0.06] to-transparent pointer-events-none" />
                    
                    <div className="relative">
                      <h3 className="font-heading font-semibold text-white text-base mb-2">⚡ Quick Response</h3>
                      <p className="text-sm text-slate-300 leading-relaxed">
                        We typically respond within 24 hours. For urgent inquiries, reach out directly via email.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </AnimatedSection>
    </div>
  );
}
